/**
 * Canvas-rendered menu with sub-screen navigation (Goal 27).
 *
 * Main Menu Layout:
 * ─────────────────────────────────────────────────────────────
 *   Row 0:  [X close] + "New Game" (center) + [Records/Fullscreen]
 *   Row 1:  [Themes] [Stats] [Game Center] — action button row
 *   Stats summary (compact)
 *   ── GAMEPLAY ──────────────────
 *        Draw   [1] [3]
 *        Flip   [Auto] [Tap]
 *   ── APPEARANCE ────────────────
 *        Hand   [Left] [Right]
 *        Back   [Pattern] [Solid]
 *   ── SOUND & FEEDBACK ──────────
 *        Sound   [On] [Off]
 *        Haptics [On] [Off]
 *
 * Themes Sub-Screen:
 * ─────────────────────────────────────────────────────────────
 *   Row 0:  [← Back] + "Themes" (center)
 *   Row 1:  Theme swatch row 1 (Midnight, Classic, Coral)
 *   Row 2:  Theme swatch row 2 (Ocean, Twilight, Forest)
 *
 * Hit areas are tracked in a module-level array, recomputed each paint.
 * Touch handling in canvasMouseMethods.ts reads these via getMenuHitAreas().
 */

import { appState } from './state'
import { themes } from './themes'
import { loadStatistics } from './statisticsManager'
import { loadRecords } from './recordsManager'
import { formatTime } from './timer'
import { isLightBackground } from './colorUtils'
import { rowY, oppositeColumnX, isLeftHanded } from './allXYCoordinates'
import { drawHeart, CARD_FONT } from './canvasMethods'
import { getTipProducts, getPurchaseStatus, isTipJarAvailable } from './tipJarManager'
import { isGameCenterAvailable } from './gameCenterManager'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface MenuHitArea {
    x: number
    y: number
    width: number
    height: number
    id: string // e.g. 'theme-HighContrastTheme', 'draw-1', 'hand-left', 'menu-themes'
    radius?: number // border radius for highlight overlay (defaults to generic borderRadius)
}

/** Sub-screens within the menu. */
export enum MenuScreen {
    Main = 'Main',
    Themes = 'Themes',
    TipJar = 'TipJar',
}

// ─── Module state ───────────────────────────────────────────────────────────

let _menuHitAreas: MenuHitArea[] = []
let _currentMenuScreen: MenuScreen = MenuScreen.Main

/** Retrieve the hit areas computed during the last drawMenu() call. */
export function getMenuHitAreas(): MenuHitArea[] {
    return _menuHitAreas
}

/** Get the current menu sub-screen. */
function getMenuScreen(): MenuScreen {
    return _currentMenuScreen
}

/** Set the current menu sub-screen. */
export function setMenuScreen(screen: MenuScreen): void {
    _currentMenuScreen = screen
}

/** Reset menu to main screen (call when menu closes). */
export function resetMenuScreen(): void {
    _currentMenuScreen = MenuScreen.Main
}

/** Currently "pressed" hit area ID — triggers a highlight overlay on next paint. */
let _pressedHitAreaId: string | null = null

/**
 * Mark a menu hit area as visually pressed.
 * Call schedulePaint() after to render the highlight, then clear after a short delay.
 */
export function setMenuPressedItem(id: string): void {
    _pressedHitAreaId = id
}

/** Clear the pressed state. Call schedulePaint() after. */
export function clearMenuPressedItem(): void {
    _pressedHitAreaId = null
}

// ─── Layout helpers ─────────────────────────────────────────────────────────

/**
 * Y position for settings row n (0 = first control in first section).
 *
 * Layout after action buttons:
 *   sectionHeaderHeight  — "GAMEPLAY" header
 *   control 0: Draw
 *   control 1: Flip
 *   sectionHeaderHeight  — "APPEARANCE" header
 *   control 2: Hand
 *   control 3: Back
 *   sectionHeaderHeight  — "SOUND & FEEDBACK" header
 *   control 4: Sound
 *   control 5: Haptics
 */
function settingsRowY(n: number): number {
    const ch = appState.cardHeight
    const spacing = appState.cardSpacing
    const segH = Math.floor(ch * 0.65)
    const sectionHeaderH = Math.floor(ch * 0.35)

    // First control starts below the action button row (Row 1)
    const actionRowBottom = rowY(1) + ch + spacing
    const firstCenterY = actionRowBottom + sectionHeaderH + spacing

    // Fixed step: segment height + spacing between each control
    const step = segH + spacing

    // Add extra space for section headers before controls 2 and 4
    let sectionOffset = 0
    if (n >= 2) sectionOffset += sectionHeaderH + Math.floor(spacing * 0.5)
    if (n >= 4) sectionOffset += sectionHeaderH + Math.floor(spacing * 0.5)

    // Return Y such that drawSegmentedControl's centering math (y + ch/2) hits the target center
    return Math.floor(firstCenterY + step * n + sectionOffset - ch / 2)
}

/** Y position for a section header above control row n. */
function sectionHeaderY(controlIndex: number): number {
    const ch = appState.cardHeight
    const sectionHeaderH = Math.floor(ch * 0.35)
    // The header sits just above the control's top edge
    const controlY = settingsRowY(controlIndex)
    const segH = Math.floor(ch * 0.65)
    const controlTopY = controlY + Math.floor((ch - segH) / 2)
    return controlTopY - sectionHeaderH
}

// ─── Drawing primitives ─────────────────────────────────────────────────────

/** Helper: draw a rounded rectangle (fill + optional stroke). */
function drawRoundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    fillStyle: string,
    strokeStyle?: string,
    lineWidth?: number
) {
    ctx.beginPath()
    ctx.roundRect(x, y, w, h, r)
    ctx.fillStyle = fillStyle
    ctx.fill()
    if (strokeStyle) {
        ctx.lineWidth = lineWidth ?? 2
        ctx.strokeStyle = strokeStyle
        ctx.stroke()
    }
}

// ─── Row 0 components ───────────────────────────────────────────────────────

/** Draw the fullscreen toggle icon at Row 0, opposite side from close button. */
function drawFullscreenIcon(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const size = appState.cardWidth
    const x = oppositeColumnX()
    const y = rowY(0)
    const isFullscreen = !!document.fullscreenElement
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'

    ctx.save()
    ctx.strokeStyle = iconColor
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const padding = size * 0.2
    const iconSize = size - padding * 2
    const centerX = x + size / 2
    const centerY = y + size / 2
    const extent = iconSize * 0.32
    const arm = iconSize * 0.18
    ctx.lineWidth = Math.max(2, size * 0.055)

    // Four corners: top-left, top-right, bottom-right, bottom-left
    const corners = [
        { cx: centerX - extent, cy: centerY - extent, dx: 1, dy: 1 },
        { cx: centerX + extent, cy: centerY - extent, dx: -1, dy: 1 },
        { cx: centerX + extent, cy: centerY + extent, dx: -1, dy: -1 },
        { cx: centerX - extent, cy: centerY + extent, dx: 1, dy: -1 },
    ]

    for (const c of corners) {
        ctx.beginPath()
        if (isFullscreen) {
            ctx.moveTo(c.cx + c.dx * arm, c.cy)
            ctx.lineTo(c.cx, c.cy)
            ctx.lineTo(c.cx, c.cy + c.dy * arm)
        } else {
            ctx.moveTo(c.cx - c.dx * arm, c.cy)
            ctx.lineTo(c.cx, c.cy)
            ctx.lineTo(c.cx, c.cy - c.dy * arm)
        }
        ctx.stroke()
    }

    ctx.restore()
    _menuHitAreas.push({ x, y, width: size, height: size, id: 'fullscreen' })
}

/** Draw the records/leaderboard toggle icon at Row 0, opposite side from close button. */
function drawMenuRecordsToggleIcon(ctx: CanvasRenderingContext2D): void {
    const size = appState.cardWidth
    const x = oppositeColumnX()
    const y = rowY(0)
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'

    ctx.save()
    ctx.strokeStyle = iconColor
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    const padding = size * 0.2
    const iconSize = size - padding * 2
    const centerX = x + size / 2
    const centerY = y + size / 2

    // Podium icon
    const baseY = centerY + iconSize * 0.3
    const sideTopY = centerY - iconSize * 0.02
    const centerTopY = centerY - iconSize * 0.28

    const sideW = iconSize * 0.24
    const centerW = iconSize * 0.26

    const centerBlockX = centerX - centerW / 2
    const leftBlockX = centerBlockX - sideW * 0.96
    const rightBlockX = centerBlockX + centerW - sideW * 0.04
    const cornerR = iconSize * 0.07

    ctx.lineWidth = Math.max(1.6, size * 0.04)

    ctx.beginPath()
    ctx.roundRect(leftBlockX, sideTopY, sideW, baseY - sideTopY, cornerR)
    ctx.stroke()

    ctx.beginPath()
    ctx.roundRect(centerBlockX, centerTopY, centerW, baseY - centerTopY, cornerR)
    ctx.stroke()

    ctx.beginPath()
    ctx.roundRect(rightBlockX, sideTopY, sideW, baseY - sideTopY, cornerR)
    ctx.stroke()

    ctx.restore()
    _menuHitAreas.push({ x, y, width: size, height: size, id: 'menu-records-toggle' })
}

/**
 * Draw a "New Game" text button in the menu at Row 0, centered horizontally.
 */
function drawNewGameTextButton(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const ch = appState.cardHeight
    const cw = appState.cardWidth
    const winW = appState.windowWidth
    const y = rowY(0)
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const highlightColor = appState.currentTheme.highlightColor

    const fontSize = Math.floor(ch * 0.22)
    const segH = Math.floor(ch * 0.65)
    const pillRadius = Math.floor(segH / 2)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    const textW = ctx.measureText('New Game').width
    ctx.restore()

    const hPad = Math.floor(cw * 0.45)
    const pillW = Math.floor(textW + hPad * 2)
    const pillX = Math.floor((winW - pillW) / 2)
    const pillY = y + Math.floor((cw - segH) / 2)

    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'
    drawRoundedRect(ctx, pillX, pillY, pillW, segH, pillRadius, pillBg)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.7
    }

    ctx.fillText('New Game', Math.floor(winW / 2), pillY + segH / 2)
    ctx.restore()

    _menuHitAreas.push({ x: pillX, y: pillY, width: pillW, height: segH, id: 'new-game', radius: pillRadius })
}

// ─── Action button row (Row 1) ──────────────────────────────────────────────

/**
 * Draw three action buttons in Row 1: Themes, Stats, Game Center.
 * Pill-shaped buttons evenly distributed across the row.
 */
function drawActionButtonRow(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const ch = appState.cardHeight
    const cw = appState.cardWidth
    const winW = appState.windowWidth
    const y = rowY(1)
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const highlightColor = appState.currentTheme.highlightColor

    const fontSize = Math.floor(ch * 0.19)
    const segH = Math.floor(ch * 0.65)
    const pillRadius = Math.floor(segH / 2)

    const buttons: { label: string; id: string }[] = [{ label: 'Themes', id: 'menu-themes' }]
    if (isGameCenterAvailable()) {
        buttons.push({ label: 'Game Center', id: 'menu-gamecenter' })
    }

    // Calculate button widths based on text
    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    const hPad = Math.floor(cw * 0.35)
    const buttonWidths = buttons.map((b) => Math.floor(ctx.measureText(b.label).width + hPad * 2))
    ctx.restore()

    const gap = Math.floor(appState.cardSpacing * 1.5)
    const totalW = buttonWidths.reduce((sum, w) => sum + w, 0) + gap * (buttons.length - 1)
    let startX = Math.floor((winW - totalW) / 2)
    const pillY = y + Math.floor((ch - segH) / 2)

    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'

    for (let i = 0; i < buttons.length; i++) {
        const btn = buttons[i]
        const pillW = buttonWidths[i]

        drawRoundedRect(ctx, startX, pillY, pillW, segH, pillRadius, pillBg)

        ctx.save()
        ctx.font = `600 ${fontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (lightBg) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
        } else {
            ctx.fillStyle = highlightColor
            ctx.globalAlpha = 0.6
        }
        ctx.fillText(btn.label, startX + pillW / 2, pillY + segH / 2)
        ctx.restore()

        _menuHitAreas.push({
            x: startX,
            y: pillY,
            width: pillW,
            height: segH,
            id: btn.id,
            radius: pillRadius,
        })

        startX += pillW + gap
    }
}

// ─── Section headers ────────────────────────────────────────────────────────

/** Draw a subtle all-caps section header label. */
function drawSectionHeader(ctx: CanvasRenderingContext2D, text: string, y: number): void {
    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const highlightColor = appState.currentTheme.highlightColor

    const fontSize = Math.floor(ch * 0.14)

    ctx.save()
    ctx.font = `700 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.35
    }
    ctx.letterSpacing = '2px'
    ctx.fillText(text, Math.floor(winW / 2), y + Math.floor(ch * 0.35) / 2)
    ctx.restore()
}

// ─── Records overlay ────────────────────────────────────────────────────────

/**
 * Draw a records/leaderboard overlay on top of the menu.
 * Shows top 10 best times with rank, penalized time, and undo count.
 */
function drawMenuRecordsOverlay(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const winH = appState.windowHeight
    const highlightColor = appState.currentTheme.highlightColor

    // Dark backdrop over menu content
    const panelPadding = Math.floor(ch * 0.3)
    const panelY = rowY(1) - Math.floor(ch * 0.15)
    const panelX = panelPadding
    const panelW = winW - panelPadding * 2
    const panelH = winH - panelY - panelPadding
    const panelR = Math.max(8, Math.floor(ch * 0.15))

    ctx.save()
    ctx.fillStyle = 'rgba(0, 0, 0, 0.88)'
    ctx.beginPath()
    ctx.roundRect(panelX, panelY, panelW, panelH, panelR)
    ctx.fill()
    ctx.restore()

    const primaryColor = 'rgba(255, 255, 255, 0.9)'
    const secondaryColor = 'rgba(255, 255, 255, 0.6)'
    const mutedColor = 'rgba(255, 255, 255, 0.35)'
    const centerX = Math.floor(winW / 2)

    const records = loadRecords()

    if (records.length === 0) {
        const msgFontSize = Math.floor(ch * 0.22)
        ctx.save()
        ctx.font = `500 ${msgFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = secondaryColor
        ctx.fillText('No completed games yet', centerX, rowY(4) + Math.floor(ch / 2))
        ctx.restore()

        // Still show stats even with no records
        drawOverlayStats(ctx, panelY, panelH, ch, centerX, secondaryColor, mutedColor)
        return
    }

    // "BEST TIMES" header
    const headerY = rowY(1) + Math.floor(ch * 0.5)
    const headerFontSize = Math.floor(ch * 0.18)

    ctx.save()
    ctx.font = `700 ${headerFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = mutedColor
    ctx.letterSpacing = '2px'
    ctx.fillText('BEST TIMES', centerX, headerY)
    ctx.restore()

    // Records list
    const listStartY = rowY(1) + Math.floor(ch * 0.85)
    const listEndY = rowY(8) + Math.floor(ch * 0.5)
    const availableHeight = listEndY - listStartY

    const recordFontSize = Math.floor(ch * 0.17)
    const lineHeight = Math.min(
        Math.floor(recordFontSize * 2.2),
        Math.floor(availableHeight / Math.min(records.length, 10))
    )

    const colWidth = Math.floor(winW * 0.8)
    const colStartX = Math.floor((winW - colWidth) / 2)
    const rankX = colStartX + Math.floor(colWidth * 0.08)
    const timeX = colStartX + Math.floor(colWidth * 0.35)
    const undoX = colStartX + Math.floor(colWidth * 0.72)

    for (let i = 0; i < records.length; i++) {
        const record = records[i]
        const y = listStartY + i * lineHeight

        if (y > listEndY) break

        const isTopThree = i < 3

        ctx.save()

        ctx.font = `${isTopThree ? '700' : '500'} ${recordFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'right'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = isTopThree ? highlightColor : mutedColor
        ctx.fillText(`${i + 1}.`, rankX, y)

        ctx.font = `${isTopThree ? '600' : '500'} ${recordFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = isTopThree ? primaryColor : secondaryColor
        ctx.fillText(formatTime(record.penalizedTime), timeX, y)

        ctx.font = `400 ${Math.floor(recordFontSize * 0.85)}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (record.undoCount > 0) {
            ctx.fillStyle = mutedColor
            ctx.fillText(`${record.undoCount} undo${record.undoCount !== 1 ? 's' : ''}`, undoX, y)
        } else {
            ctx.fillStyle = mutedColor
            ctx.fillText('clean', undoX, y)
        }

        ctx.restore()
    }

    // Stats summary at bottom of overlay
    drawOverlayStats(ctx, panelY, panelH, ch, centerX, secondaryColor, mutedColor)
}

/** Draw win/loss stats at the bottom of the records overlay. */
function drawOverlayStats(
    ctx: CanvasRenderingContext2D,
    panelY: number,
    panelH: number,
    ch: number,
    centerX: number,
    secondaryColor: string,
    mutedColor: string
): void {
    const stats = loadStatistics()
    if (stats.gamesPlayed === 0) return

    const statsFontSize = Math.floor(ch * 0.16)
    const statsLineHeight = Math.floor(statsFontSize * 1.6)
    const statsBottomY = panelY + panelH - Math.floor(ch * 0.3)

    const winRate = Math.round((stats.gamesWon / stats.gamesPlayed) * 100)
    const line1 = `${stats.gamesWon} won / ${stats.gamesPlayed} played  (${winRate}%)`

    let line2 = ''
    if (stats.bestTime !== null) {
        line2 = `Best: ${formatTime(stats.bestTime)}`
        if (stats.averageWinTime !== null) {
            line2 += `   Avg: ${formatTime(stats.averageWinTime)}`
        }
    }

    ctx.save()
    ctx.font = `500 ${statsFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = secondaryColor

    if (line2) {
        ctx.fillText(line1, centerX, statsBottomY - statsLineHeight)
        ctx.fillStyle = mutedColor
        ctx.fillText(line2, centerX, statsBottomY)
    } else {
        ctx.fillText(line1, centerX, statsBottomY)
    }
    ctx.restore()
}

// ─── Main menu drawing ──────────────────────────────────────────────────────

/**
 * Draw the main menu screen (action buttons, stats, grouped settings).
 */
function drawMainMenu(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    // ── Row 0: "New Game" text button (center) ──
    drawNewGameTextButton(ctx, borderRadius)

    // ── Row 0: Fullscreen icon (browser-only) ──
    if (!appState.isStandalone && document.fullscreenEnabled) {
        drawFullscreenIcon(ctx, borderRadius)
    }

    // ── Row 1: Action buttons ──
    drawActionButtonRow(ctx, borderRadius)

    // ── GAMEPLAY section ──
    drawSectionHeader(ctx, 'GAMEPLAY', sectionHeaderY(0))

    drawSegmentedControl(
        ctx,
        settingsRowY(0),
        'Draw',
        [
            { label: '1', id: 'draw-1', active: appState.settings.drawCount === 1 },
            { label: '3', id: 'draw-3', active: appState.settings.drawCount === 3 },
        ],
        borderRadius
    )

    drawSegmentedControl(
        ctx,
        settingsRowY(1),
        'Flip',
        [
            { label: 'Auto', id: 'flip-auto', active: appState.settings.autoFlipCards },
            { label: 'Tap', id: 'flip-tap', active: !appState.settings.autoFlipCards },
        ],
        borderRadius
    )

    // ── APPEARANCE section ──
    drawSectionHeader(ctx, 'APPEARANCE', sectionHeaderY(2))

    drawSegmentedControl(
        ctx,
        settingsRowY(2),
        'Hand',
        [
            { label: 'Left', id: 'hand-left', active: appState.settings.handedness === 'left' },
            { label: 'Right', id: 'hand-right', active: appState.settings.handedness === 'right' },
        ],
        borderRadius
    )

    drawSegmentedControl(
        ctx,
        settingsRowY(3),
        'Back',
        [
            { label: 'Pattern', id: 'back-pattern', active: appState.settings.cardBackDesign === 'pattern' },
            { label: 'Solid', id: 'back-solid', active: appState.settings.cardBackDesign === 'solid' },
        ],
        borderRadius
    )

    // ── SOUND & FEEDBACK section ──
    drawSectionHeader(ctx, 'SOUND & FEEDBACK', sectionHeaderY(4))

    drawSegmentedControl(
        ctx,
        settingsRowY(4),
        'Sound',
        [
            { label: 'On', id: 'sound-on', active: appState.settings.soundEnabled },
            { label: 'Off', id: 'sound-off', active: !appState.settings.soundEnabled },
        ],
        borderRadius
    )

    drawSegmentedControl(
        ctx,
        settingsRowY(5),
        'Haptics',
        [
            { label: 'On', id: 'haptic-on', active: appState.settings.hapticsEnabled },
            { label: 'Off', id: 'haptic-off', active: !appState.settings.hapticsEnabled },
        ],
        borderRadius
    )

    // ── Records toggle icon (always visible) ──
    drawMenuRecordsToggleIcon(ctx)

    // ── Records overlay (when toggled on) ──
    if (appState.showMenuRecords) {
        drawMenuRecordsOverlay(ctx, borderRadius)
    }

    // ── Tip jar icon (bottom corner, iOS only, drawn on top of overlay) ──
    if (isTipJarAvailable()) {
        drawTipJarIcon(ctx)
    }
}

// ─── Themes sub-screen ──────────────────────────────────────────────────────

/**
 * Draw the Themes sub-screen: back button + title + theme swatch grid.
 */
function drawThemesSubScreen(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const ch = appState.cardHeight
    const cw = appState.cardWidth
    const winW = appState.windowWidth
    const y0 = rowY(0)
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const highlightColor = appState.currentTheme.highlightColor

    // ── Row 0: Back button (left side) ──
    const fontSize = Math.floor(ch * 0.22)
    const segH = Math.floor(ch * 0.65)
    const pillRadius = Math.floor(segH / 2)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    const backTextW = ctx.measureText('← Back').width
    ctx.restore()

    const hPad = Math.floor(cw * 0.35)
    const backPillW = Math.floor(backTextW + hPad * 2)
    const backPillX = Math.floor(appState.cardSpacing)
    const backPillY = y0 + Math.floor((cw - segH) / 2)

    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'
    drawRoundedRect(ctx, backPillX, backPillY, backPillW, segH, pillRadius, pillBg)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.7
    }
    ctx.fillText('← Back', backPillX + backPillW / 2, backPillY + segH / 2)
    ctx.restore()

    _menuHitAreas.push({
        x: backPillX,
        y: backPillY,
        width: backPillW,
        height: segH,
        id: 'menu-back',
        radius: pillRadius,
    })

    // ── Row 0: "Themes" title (centered) ──
    const titleFontSize = Math.floor(ch * 0.26)
    ctx.save()
    ctx.font = `700 ${titleFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.8
    }
    ctx.fillText('Themes', Math.floor(winW / 2), y0 + Math.floor(cw / 2))
    ctx.restore()

    // ── Row 1-2: Theme swatches (2×3 grid) ──
    const themeEntries = Object.entries(themes)
    const swatchesRow1 = themeEntries.slice(0, 3)
    const swatchesRow2 = themeEntries.slice(3, 6)

    drawThemeSwatchRow(ctx, swatchesRow1, rowY(1), borderRadius)
    drawThemeSwatchRow(ctx, swatchesRow2, rowY(2), borderRadius)
}

// ─── Tip jar icon (bottom-left of menu) ─────────────────────────────────────

/** Draw a small "$" icon in the bottom-left corner of the menu screen. */
function drawTipJarIcon(ctx: CanvasRenderingContext2D): void {
    const size = appState.cardWidth
    const ch = appState.cardHeight
    const spacing = appState.cardSpacing

    // Align horizontally with the leaderboard podium icon
    const x = oppositeColumnX()
    // Mirror the podium's top offset — same distance from the bottom edge
    const y = appState.windowHeight - rowY(0) - size

    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const iconColor = lightBg ? 'rgba(0, 0, 0, 0.55)' : 'rgba(255, 255, 255, 0.55)'
    const lw = Math.max(1.8, size * 0.04)

    const centerX = x + size / 2
    const centerY = y + size / 2
    const padding = size * 0.18
    const iconW = size - padding * 2
    const iconH = iconW * 0.8

    ctx.save()
    ctx.strokeStyle = iconColor
    ctx.fillStyle = iconColor
    ctx.lineWidth = lw
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // ── Jar body (rounded rectangle) ──
    const jarW = iconW * 0.7
    const jarH = iconH * 0.7
    const jarX = centerX - jarW / 2
    const jarY = centerY - jarH / 2 + iconH * 0.15
    const jarR = jarW * 0.15

    ctx.beginPath()
    ctx.roundRect(jarX, jarY, jarW, jarH, jarR)
    ctx.stroke()

    // ── Jar lid (wider rectangle on top) ──
    const lidW = jarW * 1.1
    const lidH = jarH * 0.15
    const lidX = centerX - lidW / 2
    const lidY = jarY - lidH
    const lidR = lidH * 0.35

    ctx.beginPath()
    ctx.roundRect(lidX, lidY, lidW, lidH, lidR)
    ctx.stroke()

    // ── Coin slot (small rectangle in lid) ──
    const slotW = lidW * 0.3
    const slotH = lw * 1.2
    ctx.beginPath()
    ctx.roundRect(centerX - slotW / 2, lidY - slotH / 2, slotW, slotH, slotH / 2)
    ctx.fill()

    // ── Coin (circle above the slot, tilted) ──
    const coinR = iconW * 0.13
    const coinCX = centerX + jarW * 0.22
    const coinCY = lidY - coinR * 1.3

    ctx.beginPath()
    ctx.arc(coinCX, coinCY, coinR, 0, Math.PI * 2)
    ctx.stroke()

    // "$" on the coin
    const coinFontSize = Math.floor(coinR * 1.3)
    ctx.font = `700 ${coinFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('$', coinCX, coinCY)

    // ── "TIP" text on the jar body ──
    const tipFontSize = Math.floor(jarH * 0.3)
    ctx.font = `700 ${tipFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('TIP', centerX, jarY + jarH * 0.52)

    ctx.restore()

    _menuHitAreas.push({ x, y, width: size, height: size, id: 'menu-tipjar' })
}

// ─── Tip Jar sub-screen ─────────────────────────────────────────────────────

/**
 * Draw the Tip Jar sub-screen: back button + title + 3 purchase buttons + status.
 */
function drawTipJarSubScreen(ctx: CanvasRenderingContext2D, borderRadius: number): void {
    const ch = appState.cardHeight
    const cw = appState.cardWidth
    const winW = appState.windowWidth
    const y0 = rowY(0)
    const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
    const highlightColor = appState.currentTheme.highlightColor

    // ── Row 0: Back button (left side) ──
    const fontSize = Math.floor(ch * 0.22)
    const segH = Math.floor(ch * 0.65)
    const pillRadius = Math.floor(segH / 2)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    const backTextW = ctx.measureText('← Back').width
    ctx.restore()

    const hPad = Math.floor(cw * 0.35)
    const backPillW = Math.floor(backTextW + hPad * 2)
    const backPillX = Math.floor(appState.cardSpacing)
    const backPillY = y0 + Math.floor((cw - segH) / 2)

    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'
    drawRoundedRect(ctx, backPillX, backPillY, backPillW, segH, pillRadius, pillBg)

    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.7
    }
    ctx.fillText('← Back', backPillX + backPillW / 2, backPillY + segH / 2)
    ctx.restore()

    _menuHitAreas.push({
        x: backPillX,
        y: backPillY,
        width: backPillW,
        height: segH,
        id: 'menu-back',
        radius: pillRadius,
    })

    // ── Row 0: "Tip Jar" title (centered) ──
    const titleFontSize = Math.floor(ch * 0.26)
    ctx.save()
    ctx.font = `700 ${titleFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.8
    }
    ctx.fillText('Tip Jar', Math.floor(winW / 2), y0 + Math.floor(cw / 2))
    ctx.restore()

    // ── Subtitle text ──
    const subtitleFontSize = Math.floor(ch * 0.17)
    const subtitleY = rowY(1) + Math.floor(ch * 0.15)
    const lineH = Math.floor(subtitleFontSize * 1.5)

    ctx.save()
    ctx.font = `500 ${subtitleFontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.55
    }
    ctx.fillText('Thank you for playing!', Math.floor(winW / 2), subtitleY)
    ctx.fillText('This app will always be free.', Math.floor(winW / 2), subtitleY + lineH)
    ctx.fillText(
        'All tips are appreciated and help fund development.',
        Math.floor(winW / 2),
        subtitleY + lineH * 2
    )
    ctx.restore()

    // ── Purchase buttons (3 tiers) ──
    const products = getTipProducts()
    const purchaseStatus = getPurchaseStatus()
    const buttonFontSize = Math.floor(ch * 0.2)
    const buttonH = Math.floor(ch * 0.75)
    const buttonW = Math.floor(winW * 0.65)
    const buttonX = Math.floor((winW - buttonW) / 2)
    const buttonRadius = Math.floor(buttonH / 2)
    const buttonGap = Math.floor(appState.cardSpacing * 1.5)
    const firstButtonY = rowY(2) + Math.floor(ch * 0.4)

    const tierLabels = ['Small Tip', 'Nice Tip', 'Generous Tip']
    const tierEmojis = ['☕', '🎉', '💎']

    for (let i = 0; i < products.length && i < 3; i++) {
        const product = products[i]
        const bY = firstButtonY + i * (buttonH + buttonGap)
        const isPurchasing = purchaseStatus === 'purchasing'

        // Button background
        const btnBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'
        drawRoundedRect(ctx, buttonX, bY, buttonW, buttonH, buttonRadius, btnBg)

        // Button text: "emoji Label — $X.XX"
        ctx.save()
        ctx.font = `600 ${buttonFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        if (lightBg) {
            ctx.fillStyle = isPurchasing ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.6)'
        } else {
            ctx.fillStyle = highlightColor
            ctx.globalAlpha = isPurchasing ? 0.35 : 0.7
        }
        const label = `${tierEmojis[i]}  ${tierLabels[i]}  —  ${product.displayPrice}`
        ctx.fillText(label, buttonX + buttonW / 2, bY + buttonH / 2)
        ctx.restore()

        if (!isPurchasing) {
            _menuHitAreas.push({
                x: buttonX,
                y: bY,
                width: buttonW,
                height: buttonH,
                id: `tip-${product.id}`,
                radius: buttonRadius,
            })
        }
    }

    // ── Status message (below buttons) ──
    if (purchaseStatus !== 'idle' && purchaseStatus !== 'purchasing') {
        const statusY = firstButtonY + 3 * (buttonH + buttonGap) + Math.floor(ch * 0.1)
        const statusFontSize = Math.floor(ch * 0.19)

        ctx.save()
        ctx.font = `600 ${statusFontSize}px ${CARD_FONT}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'

        if (purchaseStatus === 'success') {
            ctx.fillStyle = highlightColor
            ctx.fillText('Thank you for your support!', Math.floor(winW / 2), statusY)
        } else if (purchaseStatus === 'cancelled') {
            if (lightBg) {
                ctx.fillStyle = 'rgba(0, 0, 0, 0.4)'
            } else {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.4)'
            }
            ctx.fillText('Purchase cancelled', Math.floor(winW / 2), statusY)
        } else if (purchaseStatus === 'error') {
            ctx.fillStyle = 'rgba(255, 100, 100, 0.8)'
            ctx.fillText('Something went wrong', Math.floor(winW / 2), statusY)
        } else if (purchaseStatus === 'pending') {
            if (lightBg) {
                ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
            } else {
                ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'
            }
            ctx.fillText('Purchase pending...', Math.floor(winW / 2), statusY)
        }

        ctx.restore()
    }
}

// ─── Public entry point ─────────────────────────────────────────────────────

/**
 * Draw the full canvas menu and populate _menuHitAreas.
 * Called from paint.ts when appState.currentAppState === Menu.
 */
export function drawMenu(ctx: CanvasRenderingContext2D): void {
    _menuHitAreas = []

    const ch = appState.cardHeight
    const borderRadius = Math.max(4, Math.floor(ch * 0.1))

    switch (_currentMenuScreen) {
        case MenuScreen.Themes:
            drawThemesSubScreen(ctx, borderRadius)
            break
        case MenuScreen.TipJar:
            drawTipJarSubScreen(ctx, borderRadius)
            break
        case MenuScreen.Main:
        default:
            drawMainMenu(ctx, borderRadius)
            break
    }

    // ── Press highlight overlay ──
    if (_pressedHitAreaId) {
        const hitArea = _menuHitAreas.find((a) => a.id === _pressedHitAreaId)
        if (hitArea) {
            const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
            const overlayColor = lightBg ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)'
            const r = hitArea.radius ?? borderRadius
            drawRoundedRect(ctx, hitArea.x, hitArea.y, hitArea.width, hitArea.height, r, overlayColor)
        }
    }
}

// ─── Theme swatch row ───────────────────────────────────────────────────────

function drawThemeSwatchRow(
    ctx: CanvasRenderingContext2D,
    entries: [string, (typeof themes)[keyof typeof themes]][],
    y: number,
    borderRadius: number
) {
    const cw = appState.cardWidth
    const ch = appState.cardHeight
    const winW = appState.windowWidth
    const gap = Math.floor(appState.cardSpacing * 1.5)

    const swatchW = cw
    const swatchH = ch
    const totalW = entries.length * swatchW + (entries.length - 1) * gap
    const startX = Math.floor((winW - totalW) / 2)

    const miniCardW = Math.floor(swatchW * 0.38)
    const miniCardH = Math.floor(miniCardW / 0.75)
    const miniRadius = Math.max(2, Math.floor(borderRadius * 0.5))

    const labelFontSize = Math.max(8, Math.floor(ch * 0.13))

    for (let i = 0; i < entries.length; i++) {
        const [themeName, theme] = entries[i]
        const sx = startX + i * (swatchW + gap)
        const isActive = themeName === appState.settings.themeName

        // Active highlight ring
        if (isActive) {
            const pad = 3
            drawRoundedRect(
                ctx,
                sx - pad,
                y - pad,
                swatchW + pad * 2,
                swatchH + pad * 2,
                borderRadius + pad,
                'transparent',
                appState.currentTheme.highlightColor,
                3
            )
        }

        // Swatch background
        ctx.save()
        ctx.beginPath()
        ctx.roundRect(sx, y, swatchW, swatchH, borderRadius)
        ctx.clip()

        ctx.fillStyle = theme.backgroundColor
        ctx.fillRect(sx, y, swatchW, swatchH)

        // Mini card back (left)
        const cardsY = y + Math.floor((swatchH - miniCardH) * 0.42)
        const cardBackX = sx + Math.floor(swatchW * 0.12)
        const cardFaceX = sx + swatchW - miniCardW - Math.floor(swatchW * 0.12)

        ctx.beginPath()
        ctx.roundRect(cardBackX, cardsY, miniCardW, miniCardH, miniRadius)
        ctx.fillStyle = theme.cardBackColor
        ctx.fill()
        ctx.lineWidth = 1
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)'
        ctx.stroke()

        // Mini card face (right)
        ctx.beginPath()
        ctx.roundRect(cardFaceX, cardsY, miniCardW, miniCardH, miniRadius)
        ctx.fillStyle = theme.cardFaceColor
        ctx.fill()
        ctx.lineWidth = 1
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)'
        ctx.stroke()

        // Mini heart
        const heartSize = Math.floor(miniCardH * 0.45)
        ctx.fillStyle = theme.redCardColor
        drawHeart(ctx, cardFaceX + miniCardW * 0.5, cardsY + miniCardH * 0.48, heartSize)

        // Theme name label
        const swatchBgLight = isLightBackground(theme.backgroundColor)
        const labelColor = swatchBgLight ? 'rgba(0, 0, 0, 0.7)' : theme.highlightColor
        ctx.font = `600 ${labelFontSize}px ${CARD_FONT}`
        ctx.fillStyle = labelColor
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillText(theme.displayName, sx + swatchW / 2, y + swatchH - Math.floor(swatchH * 0.04))

        ctx.restore()

        // Border
        const menuBgLight = isLightBackground(appState.currentTheme.backgroundColor)
        const defaultBorderAlpha = menuBgLight && swatchBgLight ? 0.25 : 0.3
        ctx.beginPath()
        ctx.roundRect(sx, y, swatchW, swatchH, borderRadius)
        ctx.lineWidth = isActive ? 2.5 : 1
        ctx.strokeStyle = isActive
            ? appState.currentTheme.highlightColor
            : `rgba(0, 0, 0, ${defaultBorderAlpha})`
        ctx.stroke()

        // Hit area
        _menuHitAreas.push({
            x: sx - 3,
            y: y - 3,
            width: swatchW + 6,
            height: swatchH + 6,
            id: `theme-${themeName}`,
        })
    }
}

// ─── Segmented control ──────────────────────────────────────────────────────

interface Segment {
    label: string
    id: string
    active: boolean
}

function drawSegmentedControl(
    ctx: CanvasRenderingContext2D,
    y: number,
    labelText: string,
    segments: Segment[],
    borderRadius: number
) {
    const cw = appState.cardWidth
    const ch = appState.cardHeight
    const winW = appState.windowWidth

    const highlightColor = appState.currentTheme.highlightColor
    const bgColor = appState.currentTheme.backgroundColor
    const lightBg = isLightBackground(bgColor)

    const fontSize = Math.floor(ch * 0.22)
    const segH = Math.floor(ch * 0.65)
    const segW = Math.floor(cw * 1.3)
    const totalSegW = segments.length * segW

    const labelW = Math.floor(cw * 1.2)
    const controlGap = Math.floor(cw * 0.3)
    const totalControlW = labelW + controlGap + totalSegW
    const controlStartX = Math.floor((winW - totalControlW) / 2)
    const segStartX = controlStartX + labelW + controlGap

    const segY = y + Math.floor((ch - segH) / 2)
    const pillRadius = Math.floor(segH / 2)

    // Label
    ctx.save()
    ctx.font = `600 ${fontSize}px ${CARD_FONT}`
    if (lightBg) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    } else {
        ctx.fillStyle = highlightColor
        ctx.globalAlpha = 0.6
    }
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    ctx.fillText(labelText, controlStartX + labelW, y + Math.floor(ch / 2))
    ctx.restore()

    // Pill background
    const pillBg = lightBg ? 'rgba(0, 0, 0, 0.08)' : 'rgba(128, 128, 128, 0.12)'
    drawRoundedRect(ctx, segStartX, segY, totalSegW, segH, pillRadius, pillBg)

    // Segments
    for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]
        const sx = segStartX + i * segW

        if (seg.active) {
            drawRoundedRect(ctx, sx + 2, segY + 2, segW - 4, segH - 4, pillRadius - 2, highlightColor)

            const hlLight = isLightBackground(highlightColor)
            ctx.save()
            ctx.font = `700 ${fontSize}px ${CARD_FONT}`
            ctx.fillStyle = hlLight ? 'rgba(0, 0, 0, 0.85)' : '#FFFFFF'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(seg.label, sx + segW / 2, segY + segH / 2)
            ctx.restore()
        } else {
            ctx.save()
            ctx.font = `500 ${fontSize}px ${CARD_FONT}`
            if (lightBg) {
                ctx.fillStyle = 'rgba(0, 0, 0, 0.45)'
            } else {
                ctx.fillStyle = highlightColor
                ctx.globalAlpha = 0.4
            }
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(seg.label, sx + segW / 2, segY + segH / 2)
            ctx.restore()
        }

        _menuHitAreas.push({
            x: sx,
            y: segY,
            width: segW,
            height: segH,
            id: seg.id,
            radius: pillRadius,
        })
    }
}
